import React, { useEffect, useState } from "react";
import { LLMCall } from "../API";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Slider,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import { useUser } from "../contexts/useUser";
import { useLLM } from "llmasaservice-client";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { ThumbDown } from "@mui/icons-material";
import { updateLLMCallQuality } from "../dataaccess/updateCallQuality";
import { useQueryClient } from "@tanstack/react-query";
import { API } from "aws-amplify";
import { BarChart } from "@mui/x-charts/BarChart";
import { downloadS3Content } from "../dataaccess/downloadS3Content";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";

interface EvaluateResponseQualityProps {
  call?: LLMCall | null;
}

const EvaluateResponseQuality: React.FC<EvaluateResponseQualityProps> = ({
  call,
}) => {
  const theme = useTheme();
  const { project } = useUser();
  const [hideSystemInstruction, setHideSystemInstruction] = useState(true);
  const [hideRagContext, setHideRagContext] = useState(true);
  const [modelStrength, setModelStrength] = useState("loading...");
  const [timings, setTimings] = useState<any[]>([]);
  const queryClient = useQueryClient();

  const [buttonGoodVariant, setButtonGoodVariant] = useState<
    "outlined" | "text" | "contained"
  >("outlined");
  const [buttonPoorVariant, setButtonPoorVariant] = useState<
    "outlined" | "text" | "contained"
  >("outlined");

  useEffect(() => {
    setButtonGoodVariant((call?.quality ?? 0) > 0 ? "contained" : "outlined");
    setButtonPoorVariant((call?.quality ?? 0) < 0 ? "contained" : "outlined");
  }, [call?.quality]);

  useEffect(() => {
    setResponse("");
    setSelectedModel(null);

    if (!call) return;
    if (!call?.prompt) return;

    // Handle S3-stored prompt
    const promptText = call.prompt?.startsWith('s3:') ? '[Prompt stored in s3]' : call.prompt;

    setModelStrength("loading...");

    // call the events api to get the model strength
    try {
      API.post("eventapi", "/recommendModelStrength", {
        body: { prompt: promptText, endpoint: "literouteer" },
      }).then((response) => {
        console.log(response);
        setModelStrength(response.body);
      });
    } catch (error) {
      console.error("Error fetching model strength", error);
    }
  }, [call]);

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const {
    response: compareResponse,
    send,
    setResponse,
    idle,
  } = useLLM({
    project_id: project?.id ?? "",
    url: url,
  });
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  useEffect(() => {
    if (call && call.timings) {
      const computeTimings = (data: any) => {
        const dataset = data.map((item: any, index: number) => {
          // If duration is already provided and item is parallel, use it directly
          if (item.duration !== undefined && item.parallel) {
            return {
              title: item.title,
              start: item.time,
              duration: item.duration / 1000,
              parallel: true,
            };
          }

          const start = new Date(item.time).getTime();

          // Find the next non-parallel entry
          let nextStart = start;
          for (let i = index + 1; i < data.length; i++) {
            if (!data[i].parallel) {
              nextStart = new Date(data[i].time).getTime();
              break;
            }
          }

          const duration = (nextStart - start) / 1000;

          return {
            title: item.title,
            start: item.time,
            duration: duration,
          };
        });

        setTimings(dataset);
      };

      try {
        computeTimings(JSON.parse(call.timings));
      } catch (error) {
        console.error("Error parsing timings", error);
      }
    } else {
      setTimings([]);
    }
  }, [call]);

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleDownload = async (fieldType: string) => {
    if (!call?.id) return;

    try {
      await downloadS3Content(call.id, fieldType);
    } catch (error) {
      console.error(`Failed to download ${fieldType}:`, error);
      // You could add a toast notification here
    }
  };

  const renderS3Content = (
    content: string | undefined,
    fieldType: string,
    label: string
  ) => {
    if (!content) return null;

    if (content.startsWith("s3://")) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            color="info.main"
            sx={{ wordBreak: "break-all" }}
          >
            <LinkIcon
              sx={{
                fontSize: 16,
                mr: 0.5,
                verticalAlign: "middle",
              }}
            />
            {content}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(fieldType)}
            sx={{ minWidth: "auto", flexShrink: 0 }}
          >
            Download {label}
          </Button>
        </Box>
      );
    }

    return content;
  };

  const renderMessagesContent = () => {
    if (!call?.messages || !call.messages.trim()) return null;

    if (call.messages.startsWith("s3://")) {
      return renderS3Content(call.messages, "messages", "Messages");
    }

    try {
      const parsedMessages = JSON.parse(call.messages);
      return (
        parsedMessages.length > 0 &&
        parsedMessages
          .filter((message: any) => (hideSystemInstruction ? message.role !== "system" : true))
          .map((message: any, index: number) => (
            <React.Fragment key={index}>
              <Typography variant="h6" mt={2}>
                {message.role}
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(message.content)}
              </Typography>
            </React.Fragment>
          ))
      );
    } catch (error) {
      return (
        <Typography variant="body2" color="error.main" mt={2}>
          Error parsing messages: {call.messages}
        </Typography>
      );
    }
  };

  return (
    <>
      <Box margin={2} maxWidth="100%">
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Quality" />
          <Tab label="Call Trace" />
        </Tabs>

        {value === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BarChart
                dataset={timings}
                yAxis={[{ scaleType: "band", dataKey: "title" }]}
                series={[
                  { dataKey: "duration", color: theme.palette.primary.main },
                ]}
                layout="horizontal"
                grid={{ vertical: true }}
                xAxis={[
                  {
                    label: "duration (seconds)",
                  },
                ]}
                //width={500}
                height={500}
                margin={{
                  left: 250,
                  right: 50,
                  top: 50,
                  bottom: 75,
                }}
              />
            </Grid>
          </Grid>
        )}

        {value === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                Response Quality - select how well the response suited the
                context and prompt
              </Typography>
              <Typography variant="body2">
                Model: {call?.service?.name ?? (call?.model?.name + " (in " + call?.model?.defaultServiceGroup + " group)")} made
                on {new Date(call?.createdAt ?? "").toLocaleString()} 
              </Typography>

              <Box p={2}>
                <Button
                  variant={buttonGoodVariant}
                  color="primary"
                  startIcon={<ThumbUpIcon />}
                  sx={{ marginRight: 2 }}
                  onClick={() => {
                    if (call) {
                      call.quality = 1;
                      updateLLMCallQuality(call?.id ?? "", 1);
                      queryClient.invalidateQueries({ queryKey: ["calls"] });
                    }
                  }}
                >
                  Good Response
                </Button>
                <Button
                  variant={buttonPoorVariant}
                  color="primary"
                  startIcon={<ThumbDown />}
                  onClick={() => {
                    if (call) {
                      call.quality = -1;
                      updateLLMCallQuality(call?.id ?? "", -1);
                      queryClient.invalidateQueries({ queryKey: ["calls"] });
                    }
                  }}
                >
                  Poor Response
                </Button>
                <Typography variant="body2">
                  Recommended model strength required: {modelStrength}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper
                sx={{ backgroundColor: theme.palette.background.default, p: 2 }}
              >
                <Typography variant="h6">Conversation</Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideSystemInstruction}
                      onChange={(e) =>
                        setHideSystemInstruction(e.target.checked)
                      }
                    />
                  }
                  label="Hide System Instruction"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideRagContext}
                      onChange={(e) => setHideRagContext(e.target.checked)}
                    />
                  }
                  label="Hide RAG Context"
                />

                {renderMessagesContent()}

                <Typography variant="h6" mt={2}>
                  prompt
                </Typography>
                {call?.prompt?.startsWith('s3://') ? (
                  renderS3Content(call.prompt, 'prompt', 'Prompt')
                ) : call?.prompt?.includes("CONTEXT:") ? (
                  <>
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {call?.prompt.split("CONTEXT:")[0].split("PROMPT:")[1] ||
                        ""}
                    </Typography>
                    {!hideRagContext && (
                      <>
                        <Typography variant="h6">context</Typography>
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {call?.prompt.split("CONTEXT:")[1]?.trim() || ""}
                        </Typography>
                      </>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {call?.prompt}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper
                sx={{ backgroundColor: theme.palette.background.default, p: 2 }}
              >
                <Typography variant="h6">Response</Typography>
                <Typography variant="body2">
                  Model: {call?.service?.name ?? call?.model?.name} (by {call?.service?.vendor ?? call?.model?.vendor})
                </Typography>
                {call?.response?.startsWith('s3://') ? (
                  renderS3Content(call.response, 'response', 'Response')
                ) : (
                  <ReactMarkdown>{call?.response}</ReactMarkdown>
                )}
              </Paper>
              <br />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper
                sx={{ backgroundColor: theme.palette.background.default, p: 2 }}
              >
                <Typography variant="h6">
                  Compare to model: &nbsp;
                  <Select
                    sx={{ width: "150px" }}
                    size="small"
                    variant="outlined"
                    value={selectedModel ?? ""}
                    onChange={(event) => {
                      const value = event.target.value as string;
                      setSelectedModel(value);
                      console.log(value);
                      send(
                        call?.prompt ?? "",
                        call?.messages ? JSON.parse(call?.messages) : [],
                        [],
                        true,
                        false,
                        value
                      );
                    }}
                    disabled={!idle}
                  >
                    {project?.services?.items
                      .filter((service) => service?.id !== call?.service?.id)
                      .map((service) => (
                        <MenuItem key={service?.id} value={service?.id}>
                          {service?.name}
                        </MenuItem>
                      ))}
                  </Select>
                </Typography>
                {!idle && <Typography variant="body2">Loading...</Typography>}
                <ReactMarkdown>{compareResponse}</ReactMarkdown>
              </Paper>
              <br />
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default EvaluateResponseQuality;
